/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Dataset Refresh History & Schedule
 * Feature #183: External Metadata Enrichment
 * Constitution: v1.19.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Clock, Calendar } from 'lucide-react';
import { useRefreshHistory, useRefreshSchedule } from '../../services/operations/metadataService';
import {
  PageLayout,
  Card,
  DataTable,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

type RefreshRun = NonNullable<ReturnType<typeof useRefreshHistory>['data']>['refreshes'][number];

function statusVariant(status: string | null): 'success' | 'error' | 'warning' | 'secondary' {
  if (!status) return 'secondary';
  if (status === 'Completed') return 'success';
  if (status === 'Failed') return 'error';
  if (status === 'InProgress') return 'warning';
  return 'secondary';
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export const RefreshHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useRefreshHistory(id ?? null, 50);
  const schedule = useRefreshSchedule(id ?? null);

  const isLoading = history.isLoading || schedule.isLoading;
  const isError = history.isError || schedule.isError;

  const handleRetry = () => {
    void history.refetch();
    void schedule.refetch();
  };

  const modelName = history.data?.modelName ?? id ?? '—';
  const refreshes = history.data?.refreshes ?? [];
  const scheduleData = schedule.data?.schedule;

  const columns: DataTableColumn<RefreshRun>[] = [
    {
      key: 'status',
      header: 'Status',
      width: '14%',
      render: (r) => <Badge variant={statusVariant(r.status)}>{r.status ?? '—'}</Badge>,
    },
    {
      key: 'refreshType',
      header: 'Type',
      width: '16%',
      render: (r) => r.refreshType ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'startedAt',
      header: 'Started',
      width: '26%',
      render: (r) => (
        <span className="flex items-center gap-1.5 text-[var(--color-text-secondary)]">
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          {r.startedAt ? formatEastern(r.startedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'durationSeconds',
      header: 'Duration',
      width: '14%',
      render: (r) => formatDuration(r.durationSeconds),
    },
    {
      key: 'error',
      header: 'Error',
      width: '30%',
      render: (r) =>
        r.errorCode ? (
          <span className="text-[var(--color-status-danger)]">
            {r.errorCode}: {r.errorDescription ?? ''}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
  ];

  return (
    <PageLayout
      helpId="auto.refresh-history"
      title={modelName}
      description="Refresh history and schedule"
      icon={<RefreshCw className="h-6 w-6" />}
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState
          title="Failed to load refresh data"
          message="The refresh history or schedule could not be loaded."
          onRetry={handleRetry}
        />
      ) : !history.data ? (
        <EmptyState
          title="Model not found"
          message="This semantic model does not exist or has not been collected."
        />
      ) : (
        <div className="space-y-6">
          {/* Schedule section */}
          <Card className="p-4">
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
              <Calendar className="h-5 w-5 text-[var(--color-accent)]" />
              Refresh Schedule
            </h2>
            {!scheduleData ? (
              <p className="text-sm text-[var(--color-text-secondary)]">
                No refresh schedule configured. DirectQuery and Direct Lake models do not require
                scheduled refresh.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium uppercase text-[var(--color-text-secondary)]">
                    Status
                  </p>
                  <div className="mt-1">
                    <Badge variant={scheduleData.isEnabled ? 'success' : 'secondary'}>
                      {scheduleData.isEnabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-[var(--color-text-secondary)]">
                    Days
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {scheduleData.days?.join(', ') ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-[var(--color-text-secondary)]">
                    Times (UTC)
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {scheduleData.times?.join(', ') ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-[var(--color-text-secondary)]">
                    Timezone
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {scheduleData.localTimezoneId ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase text-[var(--color-text-secondary)]">
                    Notify
                  </p>
                  <p className="mt-1 text-sm text-[var(--color-text-primary)]">
                    {scheduleData.notifyOption ?? '—'}
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* History section */}
          <div>
            <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
              <RefreshCw className="h-5 w-5 text-[var(--color-accent)]" />
              Refresh History
              <span className="text-sm font-normal text-[var(--color-text-secondary)]">
                ({refreshes.length} runs)
              </span>
            </h2>
            <Card padding="none">
              <DataTable
                columns={columns}
                rows={refreshes}
                rowKey={(r) => r.refreshRequestId}
                emptyMessage="No refresh runs have been collected yet."
                minWidth="44rem"
              />
            </Card>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default RefreshHistoryPage;
